/**
 * Planning Section for Project Part Detail Page
 * Displays start and end date inputs
 */

import React from "react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { ProjectPart } from "shared/types/renovation";

interface PartPlanningSectionProps {
  part: ProjectPart;
  onFieldChange: (field: string, value: unknown) => void;
}

// Format date helper
const formatDate = (date: Date | string | undefined) => {
  if (!date) return "";
  if (date instanceof Date) {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const day = String(date.getDate()).padStart(2, "0");
    return `${year}-${month}-${day}`;
  }
  return typeof date === "string" ? date.split("T")[0] : "";
};

export const PartPlanningSection: React.FC<PartPlanningSectionProps> = ({
  part,
  onFieldChange,
}) => {
  return (
    <div className="rounded-lg border border-neutral-200 bg-white p-4">
      <div className="mb-4 border-b border-neutral-100 pb-2">
        <h4 className="text-sm font-medium text-neutral-700">Planning</h4>
      </div>

      <div className="grid grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label htmlFor="part-start-date">Startdatum</Label>
          <Input
            id="part-start-date"
            type="date"
            value={formatDate(part.plannedStartDate)}
            onChange={(e) => onFieldChange("plannedStartDate", e.target.value)}
          />
        </div>

        <div className="space-y-2">
          <Label htmlFor="part-end-date">Einddatum</Label>
          <Input
            id="part-end-date"
            type="date"
            value={formatDate(part.plannedEndDate)}
            onChange={(e) => onFieldChange("plannedEndDate", e.target.value)}
          />
        </div>
      </div>

      {/* Duration info if both dates are set */}
      {part.plannedStartDate && part.plannedEndDate && (
        <div className="mt-4 rounded-md bg-neutral-50 p-3">
          <p className="text-sm text-neutral-600">
            <span className="font-medium">Duur:</span>{" "}
            {(() => {
              const start = new Date(part.plannedStartDate);
              const end = new Date(part.plannedEndDate);
              const diffTime = Math.abs(end.getTime() - start.getTime());
              const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

              if (diffDays === 0) return "Dezelfde dag";
              if (diffDays === 1) return "1 dag";
              if (diffDays < 7) return `${diffDays} dagen`;
              if (diffDays < 30) {
                const weeks = Math.floor(diffDays / 7);
                const days = diffDays % 7;
                return `${weeks} ${weeks === 1 ? "week" : "weken"}${days > 0 ? ` en ${days} ${days === 1 ? "dag" : "dagen"}` : ""}`;
              }
              const months = Math.floor(diffDays / 30);
              const remainingDays = diffDays % 30;
              return `${months} ${months === 1 ? "maand" : "maanden"}${remainingDays > 0 ? ` en ${remainingDays} dagen` : ""}`;
            })()}
          </p>
        </div>
      )}
    </div>
  );
};

export default PartPlanningSection;
